import { useRef, useState } from "react";
import { cn } from "../lib/cn";
import { controlBase, controlOpen, controlSizes, type ControlSize } from "../lib/control";
import { useIsMobile } from "../lib/use-is-mobile";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";
import { ScrollArea } from "./layout/scroll-area";
import { useFieldControl } from "./field";
import { Popover } from "./popover";

export interface TimePickerProps {
  value?: string; // Format HH:mm e.g. "14:30"
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  size?: ControlSize;
  disabled?: boolean;
  stepMinutes?: 1 | 5 | 10 | 15;
  className?: string;
  id?: string;
}

const PRESET_TIMES = ["09:00", "12:00", "14:00", "18:00"];

/**
 * Sélecteur d'heure 100% fait maison.
 * Remplace l'input HTML5 natif par un sélecteur fluide à colonnes d'heures et minutes.
 */
export function TimePicker({
  value,
  defaultValue = "12:00",
  onChange,
  placeholder = "HH:mm",
  size = "md",
  disabled,
  stepMinutes = 5,
  className,
  id,
  ...props
}: TimePickerProps) {
  const [internal, setInternal] = useState<string>(defaultValue);
  const isControlled = value !== undefined;
  const currentTime = isControlled ? value : internal;

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fieldProps = useFieldControl(props);
  const isMobile = useIsMobile();

  const parts = currentTime.split(":");
  const currentHour = parts[0] ? parts[0].padStart(2, "0") : "12";
  const currentMinute = parts[1] ? parts[1].padStart(2, "0") : "00";

  const selectTime = (timeStr: string) => {
    if (!isControlled) setInternal(timeStr);
    onChange?.(timeStr);
  };

  const setHour = (h: string) => {
    selectTime(`${h}:${currentMinute}`);
  };

  const setMinute = (m: string) => {
    selectTime(`${currentHour}:${m}`);
  };

  // Heures (00 à 23)
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

  // Minutes (00 à 59 selon le step)
  const minutes = Array.from({ length: Math.floor(60 / stepMinutes) }, (_, i) =>
    String(i * stepMinutes).padStart(2, "0"),
  );

  const timeContent = (
    <div>
      <div className="flex items-center justify-between border-b border-border pb-2">
        <span className="text-xs font-semibold uppercase text-fg-muted">Saisir l'heure</span>
        <span className="font-mono text-sm font-bold text-accent">{currentTime}</span>
      </div>

      {/* Colonnes d'heures et minutes */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase text-fg-muted">Heure</p>
          <ScrollArea maxHeight="max-h-48" scrollbarVariant="thin" className="rounded-md border border-border p-1">
            <div className="flex flex-col gap-0.5">
              {hours.map((h) => {
                const isSelected = h === currentHour;
                return (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHour(h)}
                    className={cn(
                      "rounded px-2 py-1.5 text-center font-mono text-sm font-medium transition-colors touch-manipulation",
                      isSelected
                        ? "bg-accent font-bold text-fg-on-accent shadow-xs"
                        : "text-fg hover:bg-surface-sunken",
                    )}
                  >
                    {h} h
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase text-fg-muted">Minute</p>
          <ScrollArea maxHeight="max-h-48" scrollbarVariant="thin" className="rounded-md border border-border p-1">
            <div className="flex flex-col gap-0.5">
              {minutes.map((m) => {
                const isSelected = m === currentMinute;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMinute(m)}
                    className={cn(
                      "rounded px-2 py-1.5 text-center font-mono text-sm font-medium transition-colors touch-manipulation",
                      isSelected
                        ? "bg-accent font-bold text-fg-on-accent shadow-xs"
                        : "text-fg hover:bg-surface-sunken",
                    )}
                  >
                    {m} min
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Raccourcis rapides */}
      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        {PRESET_TIMES.map((time) => (
          <Button key={time} size="xs" variant="ghost" onClick={() => selectTime(time)}>
            {time}
          </Button>
        ))}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          controlBase,
          controlSizes[size],
          "flex cursor-pointer items-center justify-between gap-2 text-left touch-manipulation",
          isOpen && controlOpen,
          className,
        )}
        {...fieldProps}
      >
        <span className={cn("truncate font-mono font-semibold", !currentTime && "text-fg-muted")}>
          {currentTime || placeholder}
        </span>
        <svg
          className="size-4 shrink-0 text-fg-subtle"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7" />
          <path d="M10 6v4l2.5 2.5" />
        </svg>
      </button>

      {/* Affichage Mobile : Vrai BottomSheet Vaul swipable */}
      {isMobile ? (
        <BottomSheet
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title="Sélectionner l'heure"
        >
          {timeContent}
        </BottomSheet>
      ) : (
        /* Affichage Desktop : panneau flottant portalisé dans la top layer */
        <Popover anchorRef={containerRef} open={isOpen} onClose={() => setIsOpen(false)} width={256}>
          <div className="p-4">{timeContent}</div>
        </Popover>
      )}
    </div>
  );
}
